import React, { useState, useEffect } from 'react';
import { clientsApi, auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface CreateAuditFormProps {
  user: User;
  onSuccess: () => void;
  onCancel: () => void;
  auditId?: number; // Si se pasa, entra en modo edición
}

interface Client {
  id: number;
  nombre: string;
  email: string;
  telefono?: string;
  empresa?: string;
  direccion?: string;
  costoAuditoria?: number;
  tiempoEstimado?: string;
  comisionOperador?: number;
  montoFijoOperador?: number;
  tipoComision?: string;
  branches?: Array<{
    id: number;
    nombre: string;
    direccion: string;
    telefono?: string;
    precio_normal?: number;
    precio_prioritaria?: number;
    precio_urgente?: number;
  }>;
  negociosHijos?: Array<{
    id: number;
    nombre: string;
    branches?: Array<{
      id: number;
      nombre: string;
      direccion: string;
      telefono?: string;
    }>;
  }>;
}

const CreateAuditForm: React.FC<CreateAuditFormProps> = ({ user, onSuccess, onCancel, auditId }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [formData, setFormData] = useState({
    clienteId: '',
    branchId: '',
    titulo: '',
    descripcion: '',
    notas: '',
    fechaSolicitada: new Date().toISOString().split('T')[0],
    prioridad: 'normal' as 'normal' | 'prioritaria' | 'urgente',
    tiempoEstimadoHoras: '8',
    observaciones: '',
    costo: ''
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<Array<{nombreArchivo: string, nombreOriginal: string, tamaño: number}>>([]);
  const [tiempoManualmenteModificado, setTiempoManualmenteModificado] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  // Cargar datos de auditoría si estamos en modo edición
  useEffect(() => {
    if (auditId && clients.length > 0) {
      loadAuditData(auditId);
    }
  }, [auditId, clients]);

  const loadAuditData = async (id: number) => {
    try {
      const response = await auditsApi.getById(id);
      if (response.data.success) {
        const audit = response.data.data;

        // Buscar el cliente correspondiente
        const cliente = clients.find(c => c.id === audit.clientId);
        if (cliente) {
          setSelectedClient(cliente);
          setClientSearchTerm(cliente.nombre);

          setFormData({
            clienteId: audit.clientId.toString(),
            branchId: audit.branchId?.toString() || '',
            titulo: audit.titulo || '',
            descripcion: audit.descripcion || '',
            notas: audit.notas || '',
            fechaSolicitada: audit.fechaSolicitada || '',
            prioridad: audit.prioridad || 'normal',
            tiempoEstimadoHoras: audit.tiempoEstimadoHoras?.toString() || '8',
            observaciones: audit.observaciones || '',
            costo: audit.costo?.toString() || ''
          });

          // Cargar archivos adjuntos existentes
          if (audit.archivosAdjuntos && Array.isArray(audit.archivosAdjuntos)) {
            setExistingFiles(audit.archivosAdjuntos);
          }
        }
      }
    } catch (error) {
      console.error('Error cargando auditoría:', error);
      alert('Error cargando datos de la auditoría');
    }
  };

  // Efecto para cerrar el dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showClientDropdown &&
          !target.closest('.client-search-container') &&
          !target.closest('.client-option')) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientDropdown]);

  const loadClients = async () => {
    try {
      const response = await clientsApi.getForAudits();

      if (response.data.success) {
        // Procesar clientes y sus negocios hijos
        const clientsWithBranches = response.data.data.map((client: Client) => {
          let allBranches = [...(client.branches || [])];
          
          // Si el cliente tiene negocios hijos, agregar sus sucursales también
          if (client.negociosHijos && client.negociosHijos.length > 0) {
            client.negociosHijos.forEach((negocio: any) => {
              if (negocio.branches && negocio.branches.length > 0) {
                // Agregar las sucursales del negocio hijo con prefijo del nombre del negocio
                const negocioBranches = negocio.branches.map((branch: any) => ({
                  ...branch,
                  nombre: `${negocio.nombre} - ${branch.nombre}`,
                  negocioHijoId: negocio.id,
                  negocioHijoNombre: negocio.nombre
                }));
                allBranches = [...allBranches, ...negocioBranches];
              }
            });
          }
          
          // Si no tiene ninguna sucursal, crear una por defecto
          if (allBranches.length === 0) {
            allBranches = [
              {
                id: -1,
                nombre: 'Oficina Principal',
                direccion: client.direccion || 'Dirección no especificada',
                telefono: client.telefono
              }
            ];
          }
          
          return {
            ...client,
            branches: allBranches
          };
        });
        setClients(clientsWithBranches);
        setFilteredClients(clientsWithBranches);
      } else {
        alert('Error obteniendo clientes: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      alert('Error cargando clientes: ' + (error as any)?.message || 'Error de conexión');
    }
  };

  // Función para filtrar clientes por búsqueda
  const handleClientSearch = (searchTerm: string) => {
    setClientSearchTerm(searchTerm);
    setShowClientDropdown(true);

    if (searchTerm.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => {
        const search = searchTerm.toLowerCase();

        // Buscar por nombre de cliente o empresa
        const matchesClient = client.nombre.toLowerCase().includes(search) ||
          (client.empresa && client.empresa.toLowerCase().includes(search));

        // Buscar por nombre de sucursal
        const matchesBranch = client.branches?.some(branch =>
          branch.nombre.toLowerCase().includes(search)
        );

        return matchesClient || matchesBranch;
      });
      setFilteredClients(filtered);
    }
  };

  // Función para seleccionar un cliente desde la búsqueda
  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setClientSearchTerm(client.nombre);
    setFormData({ ...formData, clienteId: client.id.toString(), branchId: '' });
    setShowClientDropdown(false);
  };

  const handleClientChange = (clienteId: string) => {
    const cliente = clients.find(c => c.id === parseInt(clienteId));
    setSelectedClient(cliente || null);
    
    // Calcular tiempo estimado desde la configuración del cliente
    let tiempoEstimadoHoras = '8'; // default
    if (cliente?.tiempo_auditoria_cliente) {
      // tiempo_auditoria_cliente ya está en minutos, convertir a horas
      const minutos = cliente.tiempo_auditoria_cliente;
      tiempoEstimadoHoras = (minutos / 60).toString();
    }
    
    setFormData(prev => ({
      ...prev,
      clienteId,
      branchId: '', // Reset branch selection
      tiempoEstimadoHoras, // Usar tiempo del cliente
      costo: cliente?.costoAuditoria?.toString() || '' // Usar costo del cliente
    }));

    // Reset flag cuando se selecciona cliente - tiempo es automático
    setTiempoManualmenteModificado(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index: number) => {
    setExistingFiles(files => files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.clienteId || !formData.titulo || !formData.branchId) {
      alert('Cliente, título y sucursal son obligatorios');
      return;
    }

    
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Obtener datos de la sucursal seleccionada
      const selectedBranch = selectedClient?.branches?.find(b => b.id.toString() === formData.branchId);
      
      // Determinar el cliente correcto: si es una sucursal de negocio hijo, usar el ID del negocio hijo
      const finalClienteId = selectedBranch?.negocioHijoId || formData.clienteId;
      
      // Agregar datos del formulario (adaptado al backend)
      submitData.append('clienteId', finalClienteId);
      submitData.append('sucursal', selectedBranch?.nombre || 'Oficina Principal');
      submitData.append('direccion', selectedBranch?.direccion || selectedClient?.direccion || '');
      
      // Solo enviar branchId si no es temporal (-1)
      if (selectedBranch && selectedBranch.id !== -1) {
        submitData.append('branchId', selectedBranch.id.toString());
      }
      submitData.append('fechaSolicitada', formData.fechaSolicitada);
      submitData.append('prioridad', formData.prioridad);
      if (formData.observaciones) submitData.append('observaciones', formData.observaciones);
      
      // Campos adicionales del proyecto anterior
      if (formData.titulo) submitData.append('titulo', formData.titulo);
      if (formData.descripcion) submitData.append('descripcion', formData.descripcion);
      if (formData.notas) submitData.append('notas', formData.notas);
      // Solo enviar tiempoEstimadoHoras si es admin y lo modificó manualmente
      if (user.role === 'admin' && tiempoManualmenteModificado && formData.tiempoEstimadoHoras) {
        submitData.append('tiempoEstimadoHoras', formData.tiempoEstimadoHoras);
        console.log(`[FRONTEND] Enviando tiempo manual del admin: ${formData.tiempoEstimadoHoras}h`);
      } else {
        console.log(`[FRONTEND] No enviando tiempo - usar configuración del cliente`);
      }
      
      // Agregar archivos nuevos
      selectedFiles.forEach(file => {
        submitData.append('files', file);
      });

      // Si estamos editando, enviar archivos existentes que se deben mantener
      if (auditId && existingFiles.length > 0) {
        submitData.append('existingFiles', JSON.stringify(existingFiles));
      }

      // Log datos que se envían
      for (let pair of submitData.entries()) {
      }

      // Para FormData, necesitamos usar fetch con la URL completa
      const token = localStorage.getItem('token');
      const baseUrl = window.location.hostname === 'localhost' ?
        'http://localhost:4000/api/audits' :
        `${window.location.protocol}//${window.location.host}/api/audits`;

      const apiUrl = auditId ? `${baseUrl}/${auditId}` : baseUrl;
      const method = auditId ? 'PUT' : 'POST';

      const response = await fetch(apiUrl, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          // No establecer Content-Type para que el browser lo haga automáticamente para FormData
        },
        body: submitData
      });
      
      
      const result = await response.json();

      if (response.ok && result.success) {
        alert(auditId ? '✅ Auditoría actualizada exitosamente' : '✅ Auditoría creada exitosamente');
        onSuccess();
      } else {
        console.error('❌ Error en respuesta:', { status: response.status, result });
        alert('❌ Error: ' + (result.message || result.error || `No se pudo ${auditId ? 'actualizar' : 'crear'} la auditoría`));
      }
    } catch (error: any) {
      console.error('Error creando auditoría:', error);
      alert('❌ Error: ' + (error.message || 'Error interno del servidor'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex-responsive" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
          {auditId ? '✏️ Editar Auditoría' : '✨ Crear Nueva Auditoría'}
        </h2>
        <button
          onClick={onCancel}
          className="btn btn-secondary"
          style={{ fontSize: '0.875rem' }}
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Cliente */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              👤 Cliente *
            </label>
            <div className="client-search-container" style={{ position: 'relative' }}>
              <input
                type="text"
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIndex(prev => prev < filteredClients.length - 1 ? prev + 1 : prev);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (selectedIndex >= 0 && filteredClients[selectedIndex]) {
                      handleSelectClient(filteredClients[selectedIndex]);
                    }
                  } else if (e.key === 'Escape') {
                    setShowClientDropdown(false);
                    setSelectedIndex(-1);
                  }
                }}
                placeholder="Buscar cliente por nombre..."
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              />

              {/* Dropdown de resultados */}
              {showClientDropdown && filteredClients.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 9999,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  pointerEvents: 'auto'
                }}>
                  {filteredClients.map((cliente, index) => (
                    <div
                      key={cliente.id}
                      className="client-option"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectClient(cliente);
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                      style={{
                        padding: '0.75rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.15s',
                        backgroundColor: selectedIndex === index ? '#e5e7eb' : 'white'
                      }}
                    >
                      <div style={{ fontWeight: '500' }}>{cliente.nombre}</div>
                      {cliente.empresa && cliente.empresa !== cliente.nombre && (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {cliente.empresa}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Mensaje cuando no hay resultados */}
              {showClientDropdown && clientSearchTerm && filteredClients.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  padding: '0.75rem',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  zIndex: 10
                }}>
                  No se encontraron clientes que coincidan con "{clientSearchTerm}"
                </div>
              )}

              {/* Mensaje cuando no hay clientes cargados */}
              {showClientDropdown && !clientSearchTerm && clients.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderTop: 'none',
                  borderRadius: '0 0 0.375rem 0.375rem',
                  padding: '0.75rem',
                  color: '#ef4444',
                  fontSize: '0.875rem',
                  zIndex: 10
                }}>
                  ⚠️ No hay clientes disponibles. Contacte al administrador.
                </div>
              )}
            </div>
            
            {selectedClient && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '0.375rem',
                fontSize: '0.875rem'
              }}>
                <div><strong>📧 Email:</strong> {selectedClient.email || 'No disponible'}</div>
                {selectedClient.telefono && <div><strong>📞 Teléfono:</strong> {selectedClient.telefono}</div>}
                {selectedClient.empresa && <div><strong>🏢 Empresa:</strong> {selectedClient.empresa}</div>}
                {selectedClient.direccion && <div><strong>📍 Dirección:</strong> {selectedClient.direccion}</div>}
              </div>
            )}
          </div>

          {/* Sucursal */}
          {selectedClient && (
            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                🏢 Sucursal *
              </label>
              <select
                value={formData.branchId}
                onChange={(e) => setFormData(prev => ({ ...prev, branchId: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
              >
                <option value="">Seleccionar sucursal</option>
                {selectedClient.branches?.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.nombre} - {branch.direccion}
                  </option>
                ))}
              </select>
              <small style={{color: '#6b7280', fontSize: '0.85rem', display: 'block', marginTop: '4px'}}>
                💡 La dirección se usa automáticamente desde los datos del cliente
              </small>
            </div>
          )}

          {/* Título */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📋 Título de la auditoría *
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
              placeholder="Ej: Auditoría fiscal 2024, Revisión de procesos..."
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Fecha Solicitada */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📅 Fecha Solicitada *
            </label>
            <input
              type="date"
              value={formData.fechaSolicitada}
              onChange={(e) => setFormData(prev => ({ ...prev, fechaSolicitada: e.target.value }))}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Prioridad */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              🎯 Prioridad *
            </label>
            <select
              value={formData.prioridad}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                prioridad: e.target.value as 'normal' | 'prioritaria' | 'urgente'
              }))}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem'
              }}
            >
              <option value="normal">🟢 Normal</option>
              <option value="prioritaria">🟡 Prioritaria</option>
              <option value="urgente">🔴 Urgente</option>
            </select>

            {/* Price Preview - Solo visible para clientes */}
            {user.role === 'cliente' && selectedClient && formData.branchId && (() => {
              const selectedBranch = selectedClient.branches?.find(b => b.id.toString() === formData.branchId);

              // Determinar el precio según la prioridad
              let precioEstimado = 0;

              if (selectedBranch) {
                const precioNormalSucursal = parseFloat(String(selectedBranch.precio_normal || 0));
                const precioPrioritariaSucursal = parseFloat(String(selectedBranch.precio_prioritaria || 0));
                const precioUrgenteSucursal = parseFloat(String(selectedBranch.precio_urgente || 0));

                // Detectar si la sucursal tiene precios personalizados (diferentes de los defaults 100/150/200)
                const esValorDefecto = (precioNormalSucursal === 100 && precioPrioritariaSucursal === 150 && precioUrgenteSucursal === 200);

                if (!esValorDefecto && precioNormalSucursal > 0) {
                  // Usar precios de la sucursal
                  if (formData.prioridad === 'normal') {
                    precioEstimado = precioNormalSucursal;
                  } else if (formData.prioridad === 'prioritaria') {
                    precioEstimado = precioPrioritariaSucursal;
                  } else if (formData.prioridad === 'urgente') {
                    precioEstimado = precioUrgenteSucursal;
                  }
                } else {
                  // Heredar del cliente principal
                  const costoBase = parseFloat(String(selectedClient.costoAuditoria || 0));
                  if (formData.prioridad === 'normal') {
                    precioEstimado = costoBase;
                  } else if (formData.prioridad === 'prioritaria') {
                    precioEstimado = costoBase * 1.5;
                  } else if (formData.prioridad === 'urgente') {
                    precioEstimado = costoBase * 2;
                  }
                }
              }

              return precioEstimado > 0 ? (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '0.5rem',
                  color: 'white'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>💰</span>
                    <strong style={{ fontSize: '1rem' }}>Precio estimado de esta auditoría</strong>
                  </div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>
                    ${precioEstimado.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.9 }}>
                    * Precio sin IVA
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {/* Campos solo para admin */}
          {user.role === 'admin' && (
            <>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                  💰 Costo estimado
                </label>
                <input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  placeholder="Ej: 15000"
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                  ⏱️ Tiempo estimado (horas)
                </label>
                <input
                  type="number"
                  value={formData.tiempoEstimadoHoras}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, tiempoEstimadoHoras: e.target.value }));
                    setTiempoManualmenteModificado(true);
                  }}
                  placeholder="Ej: 8"
                  min="0.5"
                  step="0.5"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </>
          )}

          {/* Descripción */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📝 Descripción de la auditoría
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Describe los objetivos y alcance de la auditoría..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Notas */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📋 Notas e instrucciones
            </label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
              placeholder="Instrucciones específicas, metodología a seguir, consideraciones especiales..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Archivos adjuntos */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              📎 Archivos adjuntos
            </label>
            <div style={{
              border: '2px dashed #d1d5db',
              borderRadius: '0.375rem',
              padding: '1.5rem',
              textAlign: 'center',
              backgroundColor: '#f9fafb'
            }}>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="file-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
              />
              <label htmlFor="file-upload" style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>
                📎 Seleccionar archivos
              </label>
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                PDF, Word, Excel, imágenes, ZIP
              </div>
            </div>
            
            {/* Archivos existentes (en modo edición) */}
            {existingFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  📁 Archivos actuales ({existingFiles.length}):
                </p>
                {existingFiles.map((file, index) => (
                  <div key={`existing-${index}`} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '0.375rem',
                    marginBottom: '0.5rem',
                    border: '1px solid #bae6fd'
                  }}>
                    <span style={{ fontSize: '0.875rem' }}>
                      📄 {file.nombreOriginal} ({(file.tamaño / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      onClick={() => removeExistingFile(index)}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Archivos nuevos seleccionados */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  📁 Archivos nuevos ({selectedFiles.length}):
                </p>
                {selectedFiles.map((file, index) => (
                  <div key={`new-${index}`} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: '#dcfce7',
                    borderRadius: '0.375rem',
                    marginBottom: '0.5rem',
                    border: '1px solid #bbf7d0'
                  }}>
                    <span style={{ fontSize: '0.875rem' }}>
                      📄 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Observaciones finales */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              💬 Observaciones adicionales
            </label>
            <textarea
              value={formData.observaciones}
              onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
              placeholder="Cualquier observación adicional..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Botones */}
          <div className="flex-responsive" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              ❌ Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.clienteId || !formData.titulo || !formData.branchId}
              style={{
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (auditId ? '⏳ Actualizando...' : '⏳ Creando...') : (auditId ? '✅ Actualizar Auditoría' : '✅ Crear Auditoría')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateAuditForm;